import { useState } from "react";
import { Check, Copy } from "lucide-react";

import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { cn } from "@/core/utils/func/cn";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";

type CopyButtonProps = {
  /** Valeur copiée dans le presse-papiers (UUID, référence, etc.). */
  value: string;
  className?: string;
};

/**
 * Icône de copie compacte (cellules d'identifiants). Clipboard + toast,
 * même flux que la copie de clé API / ShareablePreview.
 */
export function CopyButton({ value, className }: CopyButtonProps) {
  const { t } = useTypedTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!value) return;
    if (!("clipboard" in navigator) || !navigator.clipboard?.writeText) {
      notifSilenceError("shareablePreviewClipboardUnsupported");
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      notifSilence("common.copied");
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      notifSilenceError("shareablePreviewClipboardError");
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7 shrink-0 text-muted-foreground", className)}
      onClick={(event) => {
        event.stopPropagation();
        void copy();
      }}
      aria-label={t("common.copy")}
      title={t("common.copy")}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}
